import * as Sentry from '@sentry/browser';

export function setUpSentryLoggingService({loggingDsn, application, env}) {
    const DSN = loggingDsn;
    const TAGS = {
        application,
        layer: 'client',
    };

    // see @sentry/types/dist/event.d.ts for the definition of what an event is
    function shouldIgnoreEvent(event) {
        const exception = event.exception && event.exception.values && event.exception.values[0];
        if (!exception) {
            return false;
        }

        // not stacktrace: likely a browser error, not our problem
        if (!exception.stacktrace || !exception.stacktrace.frames || exception.stacktrace.frames.length === 0) {
            return true;
        }

        const stracktraceFilesToExclude = [/addthis_widget\.js/, /\/ext\/.*.js/, /\/frame\..*\.js/];
        const stackFrames = exception.stacktrace.frames;
        if (stackFrames.some((frame) => frame.filename && stracktraceFilesToExclude.some((re) => re.test(frame.filename)))) {
            return true;
        }

        const urlsToExclude = [/abtesty\.com/];
        const latestXhrOrFetchUrls = (event.breadcrumbs || [])
            // bc.category is one of: 'fetch', 'xhr'
            .filter((bc) => bc.type === 'http' && (bc.data || {}).url)
            .map((bc) => bc.data.url);
        const latestXhrOrFetchUrl = latestXhrOrFetchUrls.length && latestXhrOrFetchUrls[latestXhrOrFetchUrls.length - 1];
        if (latestXhrOrFetchUrl && urlsToExclude.some((re) => re.test(latestXhrOrFetchUrl))) {
            return true;
        }

        return false;
    }

    function initLoggingService() {
        Sentry.init({
            dsn: DSN,
            ignoreErrors: [
                /:\sUET/,
                /'?UET'?\s/,
                /Can't find variable: heap/,
                /Erreur non spécifiée/i,
                /unspecified error/i,
                /Non-Error exception captured with keys/,
                /NS_ERROR_.*/,
                /ReportingObserver/,
                /Failed to fetch/,
                /NetworkError when attempting to fetch resource/,
                /Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing./,
                /AlgoliaSearchRequestTimeoutError: Request timed out before getting a response/,
                /webkit\.messageHandlers\.contextMenuMessageHandler\.postMessage/,

                // the 2 following errors may appear when the server crashes somehow: it returns HTML
                /SyntaxError: illegal character\s*.*:1:0/,
                /SyntaxError: Unexpected token '<'/,

                /AbortError/,
                /UnknownError/,
                /Permission denied to access property "getAttribute"/,
                /ResizeObserver loop completed with undelivered notifications/,

                // This issue is due to Google Translate trying to translate elements in svg, unfortunately, `className.indexOf` does
                // not exist on these elements...
                // https://medium.com/@amir.harel/a-b-target-classname-indexof-is-not-a-function-at-least-not-mine-8e52f7be64ca
                // https://github.com/airbnb/lottie-web/issues/1134
                /className\.indexOf is not a function/,
            ],
            environment: env,
            whitelistUrls: [
                // scheme: ws:// or http://, secure or not (s)
                // domain: malt.yyy or xxx.malt.yyy or xxx-malt.yyy
                // port accepted
                // followed by anything not part of host name & port
                /^(http|ws)s?:\/\/.*malt\.[^.]+(\/|\?|$)/,
            ],
            blacklistUrls: [
                // Chrome extensions
                /extensions\//i,
                /^chrome:\/\//i,
                // Safari extensions
                /safari-extension:\/\//i,
                // Ignore vendors errors
                /https:\/\/cdn\.malt\.com\/.+\/js\/chunk-vendors\..+\.js/,
            ],

            maxValueLength: 5000,

            beforeSend(event) {
                return shouldIgnoreEvent(event) ? null : event;
            },
        });
    }

    function addRelevantLogInfo() {
        Sentry.setTags(TAGS);
    }

    if (DSN) {
        initLoggingService();
        addRelevantLogInfo();

        window.captureException = (err) => Sentry.captureException(err);
    }
}
